import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download } from "lucide-react";

/** Phase 2 — HCD Standards Library admin.
 *  GC / Legal Manager / IT Admin upload baseline HCD documents (standard T&C,
 *  templates, policies). The evaluator uses these for the side-by-side
 *  comparison against vendor paper. */
const KINDS = [
  { k: "terms_addendum", label: "Standard T&C Addendum" },
  { k: "template", label: "General Agreement Template" },
  { k: "policy", label: "Policy Document" },
];

export default function StandardsAdmin() {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("terms_addendum");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/standards").then(r => setItems(r.data || []))
    .catch(() => toast.error("Failed to load standards"));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("standard_kind", kind);
      fd.append("name", name);
      fd.append("description", description);
      await api.post("/standards", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Standard uploaded");
      setFile(null); setName(""); setDescription("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this standard? It won't appear in future evaluator comparisons.")) return;
    try { await api.delete(`/standards/${id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="standards-admin">
      <div className="flex items-center gap-3 mb-5">
        <FileText className="w-5 h-5 text-[#005289]" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Phase 2 · Evaluator</div>
          <h1 className="text-2xl font-bold text-slate-900">HCD Standards Library</h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload the HCD standard T&C, general templates, and policies. The AI Contract Evaluator compares vendor paper against these clause-by-clause and flags deviations.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="standards-upload-card">
        <div className="text-xs font-bold uppercase text-slate-500 mb-3">Upload new standard</div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="text-xs">
            <span className="font-bold text-slate-700 block mb-1">Kind</span>
            <select value={kind} onChange={e => setKind(e.target.value)}
                    data-testid="standard-kind"
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded">
              {KINDS.map(k => <option key={k.k} value={k.k}>{k.label}</option>)}
            </select>
          </label>
          <label className="text-xs">
            <span className="font-bold text-slate-700 block mb-1">Display name</span>
            <input value={name} onChange={e => setName(e.target.value)}
                   data-testid="standard-name"
                   placeholder="HCD Standard T&C v3.1"
                   className="w-full h-9 px-3 text-sm border border-slate-200 rounded" />
          </label>
          <label className="text-xs col-span-2">
            <span className="font-bold text-slate-700 block mb-1">Description</span>
            <input value={description} onChange={e => setDescription(e.target.value)}
                   data-testid="standard-description"
                   placeholder="Effective Jan 2026 — includes updated indemnification"
                   className="w-full h-9 px-3 text-sm border border-slate-200 rounded" />
          </label>
          <label className="text-xs col-span-2">
            <span className="font-bold text-slate-700 block mb-1">File (PDF or DOCX)</span>
            <input type="file" accept=".pdf,.docx"
                   data-testid="standard-file"
                   onChange={e => setFile(e.target.files?.[0] || null)}
                   className="w-full text-sm" />
          </label>
        </div>
        <button onClick={upload} disabled={busy || !file}
                data-testid="standard-upload-btn"
                className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-40">
          <Upload className="w-3 h-3" /> {busy ? "Uploading…" : "Upload"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="standards-list">
        <div className="px-5 py-3 border-b border-slate-200 text-xs font-bold uppercase text-slate-500">
          Current standards ({items.length})
        </div>
        {items.length === 0 && (
          <div className="p-8 text-sm text-slate-500 text-center">No standards uploaded yet.</div>
        )}
        {items.map(s => (
          <div key={s.id} className="px-5 py-3 border-b border-slate-100 flex items-center gap-3"
               data-testid={`standard-row-${s.id}`}>
            <FileText className="w-4 h-4 text-slate-400" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{s.name}</div>
              <div className="text-[11px] text-slate-500">
                {KINDS.find(k => k.k === s.standard_kind)?.label || s.standard_kind} · uploaded by {s.uploaded_by_name} · {new Date(s.uploaded_at).toLocaleDateString()}
              </div>
              {s.description && <div className="text-[11px] text-slate-600 mt-0.5">{s.description}</div>}
            </div>
            <button onClick={() => remove(s.id)}
                    data-testid={`standard-delete-${s.id}`}
                    className="h-8 px-2 text-[11px] border border-red-200 text-red-600 rounded flex items-center gap-1 hover:bg-red-50">
              <Trash2 className="w-3 h-3" /> Deactivate
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
